import { sourceSupportedUtilityResourceFields } from '../../lib/sed/utilityPipelineDocumentProjection';
/**
 * extractFromDocument — Documents_On_Site Prompt 11.
 *
 * When a SED provider returns "unavailable" but a relevant uploaded
 * doc exists (its `sedSectionsHelpful` includes the section AND its
 * relevance score for the active report type ≥ 0.6), call into Gemini
 * to pull the section's needed fields out of the doc text. On success
 * the section becomes status='verified-from-doc' with confidence 0.7.
 * On failure the section stays unavailable — caller is the bootstrap
 * pipeline, which already tolerates empty sections.
 *
 * Pure-ish: takes the structured-JSON Gemini call as a dependency so
 * unit tests can pin a deterministic response without mocking the
 * SDK. Returns null when the doc is unusable (empty text, hit budget).
 */
import { Type, runGeminiJSON } from '../geminiClient';
import { documentBodyText, pageForCharOffset } from '../../lib/documentBody';
import { normaliseSectionPayload } from '../../lib/sed/sectionContractBase';
import type { AuthoredFieldDef } from '../../types/authoredEvidence';
import { checklistItemById } from '../../config/documentChecklists';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import { tieredModelFor } from '../../config/modelTiering';
import { DOC_EXTRACTION_TIMEOUT_MS } from './extractionTiming';
// SED D0.3 — single source of truth for the "is this a match?" floor.
// Aligns the extraction-attempt floor with the DCT match floor
// (`SUPPORTING_SCORE_THRESHOLD` = 0.4) so a doc in [0.4, 0.6) is no
// longer "matched but not extracted". Type-only import path is the
// scoring module, which lazy-imports geminiClient — no static cycle.
import { EXTRACTION_RELEVANCE_FLOOR } from '../docMatching/relevanceScoring';
// SED D0.3 — re-export so the lazy-import block in `sedBootstrap`
// (which already pulls `pickBestFallbackDoc`/`DOC_FALLBACK_CONFIDENCE`
// from here) can repoint its boost floor at the shared constant
// without adding a second dynamic import of relevanceScoring.
export { EXTRACTION_RELEVANCE_FLOOR } from '../docMatching/relevanceScoring';
import type { SiteDocument } from '../../types/siteRag';
import type { SedSectionId, StructuredEvidenceValue } from '../../types/siteEvidence';
import type { PassageOffset } from '../../types/siteContext';
import { cleanDocFileName } from '../../lib/docFileName';
import {
  getRequirementDocExtractSchema,
} from '../../lib/coverage/requirementDocExtractSchemas';
// URE-2 — intent-aware fallback picker reads the requirement→section
// map at module load. The map module is type-only on `SedSectionId`
// (no value imports back into this file) so static import is safe.
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
// DAT-FOLLOWUP-1 — site-bound state prior. Imported as a value (small
// helper set + functions, no circulars).
import {
  normaliseStateToCode,
  hasLocusBindingPhrase,
} from '../../lib/inferSiteState';
// EX-1/EX-2 — shared, context-free range/unit/plausibility validation
// primitive keyed by canonical SED field path. The DOC channel was the
// one extraction channel with NO validation: Gemini numbers landed in
// the SED verbatim. `validateValue` is the SAME primitive the
// site-context channel's `validateClaim` delegates to, so a wrong-unit /
// out-of-range / implausible-magnitude value is caught identically.
import { validateValue } from '../../lib/sed/fieldSchema';
// EX-6 — shared negation-polarity detector (lifted out of the
// site-context channel into a dependency-light module). Conservative:
// only flips a doc-channel boolean on an unambiguous co-located
// negation marker.
import { detectNegationPolarity } from '../../lib/sed/negationPolarity';
import { isMissingDocumentValue, sanitizeDocumentSectionFields } from '../../lib/sed/documentExtractSanity';
import { filterUtilityLetterFields } from '../../lib/sed/gridUtilityEvidence';
import { filterRequirementFieldsBySource } from '../../lib/sed/requirementSourceEvidence';
import { explicitWetlandPresence } from '../../lib/sed/wetlandPresence';
import {
  ATTRIBUTION_KEYS,
  ATTRIBUTION_PROMPT,
  attributionSchemaProps,
  mergeAttribution,
  readAttributionFromPayload,
  takeAttributionFromPayload,
} from '../../lib/sed/evidenceAttribution';
import type { EvidenceAttribution } from '../../types/siteEvidence';

/** Fixed confidence for doc-extracted sections. Lower than provider
 *  data (0.85-0.99) but high enough to trump 'unavailable'. */
export const DOC_FALLBACK_CONFIDENCE = 0.7;

/** Mixed native/vision documents cannot launder visual screening into 0.7 evidence. */
export function documentExtractionConfidence(doc: SiteDocument): number {
  return Math.max(doc.visualReadings?.length ?? 0, doc.visualReadingCount ?? 0) > 0 ? 0.6 : DOC_FALLBACK_CONFIDENCE;
}

/** Per-section field schemas. Kept narrow so the Gemini response
 *  validator catches malformed payloads early. Sections not listed
 *  here can't be doc-extracted (the helper returns a rejection /
 *  null).
 *
 *  SED D0.3 (extraction recall) — the per-section `required` arrays
 *  were REMOVED. They forced Gemini to emit EVERY listed field, so a
 *  doc that legitimately carries only a subset (e.g. an interconnection
 *  letter with grid voltage but no capacity) yielded a coerced /
 *  fabricated payload that the try/catch turned into a silent null —
 *  partial truth was dropped. We now keep only `properties` and accept
 *  a partial payload: the extractor returns a rejection
 *  (`reason: 'all-required-missing'`) ONLY when ZERO schema-property
 *  fields were extracted, not when one of the formerly-required fields
 *  is missing. Per-field correctness is still enforced downstream by
 *  the EX-2/EX-4 range/locus validator (`validateSectionData`) and the
 *  cross-state guard, which delete bad fields individually.
 *
 *  REF-LINEAGE REF-1 — every section schema carries three optional
 *  lineage fields (`pageNumber`, `sectionHeading`, `excerptPreview`)
 *  that Gemini emits when the doc has detectable page structure. The
 *  lineage fields are stripped off `data` and projected onto the result
 *  object before return — downstream schema validators (SED renderers,
 *  DOCX exporter) never see surprise keys. */
/**
 * QP-1 (quality pass) — how much of the document body the channel-A
 * section extractor reads. Was 8,000 chars: a flood cert's Section B or a
 * utility letter's capacity table routinely sits past 8k in a real
 * multi-page PDF, so the extractor answered from the cover page and the
 * value never landed. 60,000 chars (~15k tokens) covers essentially every
 * diligence document at Flash-tier input cost. The EX-4 locus span is
 * COUPLED to this constant — validation must see exactly what the model
 * saw, or a true value cited from page 9 gets rejected as unsupported.
 */
export const SECTION_EXTRACTION_TEXT_WINDOW = 60_000;

/**
 * P1.4 — how many additional windows of a long document get a second
 * extraction call when the head window left schema fields empty. Bounded on
 * purpose: three calls per section per document at most.
 */
export const EXTRA_EXTRACTION_WINDOWS = 2;
/** Overlap between consecutive windows so a passage split by a boundary is still seen whole. */
const EXTRACTION_WINDOW_OVERLAP = 1_000;

/** Section-specific vocabulary that steers which later windows are worth a call. */
const SECTION_WINDOW_VOCABULARY: Partial<Record<SedSectionId, string>> = {
  waterResource: 'water wastewater sewer mgd gallons treatment plant main lift station capacity provider utility',
  waterStress: 'water stress withdrawal supply basin aqueduct drought',
  environmental: 'wetland wetlands acres species habitat endangered delineation hydric jurisdictional',
  flood: 'flood zone fema base elevation floodplain sfha firm',
  grid: 'substation kv capacity utility transmission interconnection',
  electricity: 'rate tariff kwh cents retail',
  seismic: 'seismic pga sds sd1 design category site class',
  regulatory: 'zoning permit nuclear stance regulation',
  parcel: 'parcel acres owner zoning land use',
  climate: 'temperature wet bulb humidity climate',
  connectivity: 'fiber latency carrier exchange',
};

function camelToWords(key: string): string[] {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().split(/[^a-z0-9]+/).filter(w => w.length > 2);
}

export interface ExtractionWindow {
  start: number;
  end: number;
  text: string;
  score: number;
}

/**
 * P1.4 — pick the later windows of a long document most likely to carry the
 * section's facts. The head window (first `windowChars`) is always extracted
 * first; this ranks the remainder in overlapping slices by vocabulary hits
 * (schema field names split into words + a per-section vocabulary) and
 * returns up to `maxWindows` of them in document order. Pure; exported for tests.
 */
export function selectExtractionWindows(
  text: string,
  section: SedSectionId,
  windowChars: number = SECTION_EXTRACTION_TEXT_WINDOW,
  maxWindows: number = EXTRA_EXTRACTION_WINDOWS,
): ExtractionWindow[] {
  if (text.length <= windowChars || maxWindows <= 0) return [];
  const vocab = new Set<string>([
    ...sectionSchemaFieldNames(section).flatMap(camelToWords),
    ...(SECTION_WINDOW_VOCABULARY[section] ?? '').split(/\s+/).filter(Boolean),
  ]);
  const candidates: ExtractionWindow[] = [];
  let start = Math.max(0, windowChars - EXTRACTION_WINDOW_OVERLAP);
  while (start < text.length) {
    const end = Math.min(text.length, start + windowChars);
    const slice = text.slice(start, end);
    const lower = slice.toLowerCase();
    let score = 0;
    for (const term of vocab) {
      let idx = lower.indexOf(term);
      while (idx !== -1 && score < 10_000) {
        score += 1;
        idx = lower.indexOf(term, idx + term.length);
      }
    }
    candidates.push({ start, end, text: slice, score });
    if (end >= text.length) break;
    start = end - EXTRACTION_WINDOW_OVERLAP;
  }
  return candidates
    .filter(w => w.score > 0)
    .sort((a, b) => b.score - a.score || a.start - b.start)
    .slice(0, maxWindows)
    .sort((a, b) => a.start - b.start);
}

/** Outer budget covers every possible sequential window plus result processing. */
export function sectionExtractionTimeoutMs(doc: SiteDocument, section: SedSectionId): number {
  const windows = selectExtractionWindows(documentBodyText(doc).trim(), section).length;
  return DOC_EXTRACTION_TIMEOUT_MS * (1 + windows) + 1_000;
}

function isMissingExtractedValue(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    || (typeof v === 'number' && Number.isNaN(v));
}

/**
 * D10 — the section ids channel A can extract into. Exported so the
 * SED-currency planner's DOCUMENT_FED_SECTIONS can be sync-tested against
 * the real schema map instead of drifting from it.
 */
export function extractableSectionIds(): SedSectionId[] {
  return Object.keys(SECTION_SCHEMAS) as SedSectionId[];
}

// P4 — every section schema also asks who stated the facts, in what role,
// when and for which project. Metadata like the lineage triple: projected
// off `data` onto the result, never into the section payload.
const ATTRIBUTION_SCHEMA_PROPS = attributionSchemaProps(Type.STRING);

const SECTION_SCHEMAS: Partial<Record<SedSectionId, Record<string, unknown>>> = {
  climate: {
    type: Type.OBJECT,
    properties: {
      avgTemp: { type: Type.NUMBER },
      peakTemp: { type: Type.NUMBER },
      avgWetBulb: { type: Type.NUMBER },
      humidity: { type: Type.NUMBER },
      // REF-LINEAGE REF-1 — optional lineage. Emit only when the doc
      // has detectable page structure AND the value can be tied to a
      // specific page / heading / quote with high confidence.
      // Fabricating a page number is worse than omitting it.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
  grid: {
    type: Type.OBJECT,
    properties: {
      substationName: { type: Type.STRING },
      substationDistKm: { type: Type.NUMBER },
      voltage: { type: Type.STRING },
      capacity: { type: Type.STRING },
      complexity: { type: Type.STRING },
      // REF-LINEAGE REF-1 — optional lineage.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
  flood: {
    type: Type.OBJECT,
    properties: {
      floodZone: { type: Type.STRING },
      sfha: { type: Type.BOOLEAN },
      floodRisk: { type: Type.STRING },
      // REF-LINEAGE REF-1 — optional lineage.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
  environmental: {
    type: Type.OBJECT,
    properties: {
      hasCriticalHabitat: { type: Type.BOOLEAN },
      species: { type: Type.ARRAY, items: { type: Type.STRING } },
      hasWetlands: { type: Type.BOOLEAN },
      wetlandType: { type: Type.STRING },
      wetlandAcres: { type: Type.NUMBER },
      // REF-LINEAGE REF-1 — optional lineage.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
  // ── DAF-19 Wave A: high-value schemas ────────────────────────────
  // Target docs: water-rights deeds, watershed surveys, USGS NWIS
  // reports, county water-availability assessments. Field names mirror
  // `WaterStressEvidence` (`siteEvidence.ts:177-184`).
  waterStress: {
    type: Type.OBJECT,
    properties: {
      // Discrete scarcity bucket from WRI Aqueduct or local-equivalent.
      // Docs typically cite "Low / Moderate / High" verbatim.
      stressLevel: { type: Type.STRING },             // 'Low' | 'Moderate' | 'High'
      // Withdrawal-to-supply ratio when the doc cites a number (rare).
      bwsRaw: { type: Type.NUMBER },
      // Human-readable label the doc uses (e.g. "Extremely High Stress").
      bwsLabel: { type: Type.STRING },
    },
  },
  // P6 (document-intelligence plan) — municipal water supply / wastewater
  // service from a city or district utility letter or questionnaire. The
  // nested sub-objects mirror `MunicipalWaterSupply` / `WastewaterService`
  // (siteEvidence.ts). Each carries flat attribution keys (who stated it,
  // in what role, when, and whether it was prepared for THIS project) that
  // `normaliseWaterResourceExtraction` lifts into `provenance`. The honest-
  // null contract keys (waterResourceBasis / provenance / disclaimer) are
  // never part of the schema and are scrubbed if the model emits them.
  waterResource: {
    type: Type.OBJECT,
    properties: {
      municipalSupply: {
        type: Type.OBJECT,
        properties: {
          provider: { type: Type.STRING },
          sourceDescription: { type: Type.STRING },
          avgMgd: { type: Type.NUMBER },
          peakMgd: { type: Type.NUMBER },
          permittedMgd: { type: Type.NUMBER },
          excessMgd: { type: Type.NUMBER },
          storageGallons: { type: Type.NUMBER },
          nearestMainMiles: { type: Type.NUMBER },
          lineToSite: { type: Type.BOOLEAN },
          connectionType: { type: Type.STRING },
          notes: { type: Type.STRING },
          // Attribution — lifted into `provenance` by the normaliser.
          statedBy: { type: Type.STRING },
          statedByRole: { type: Type.STRING },
          organisation: { type: Type.STRING },
          statedOn: { type: Type.STRING },
          appliesToProject: { type: Type.STRING },
        },
      },
      wastewaterService: {
        type: Type.OBJECT,
        properties: {
          provider: { type: Type.STRING },
          sourceDescription: { type: Type.STRING },
          avgMgd: { type: Type.NUMBER },
          peakMgd: { type: Type.NUMBER },
          permittedMgd: { type: Type.NUMBER },
          excessMgd: { type: Type.NUMBER },
          storageGallons: { type: Type.NUMBER },
          nearestMainMiles: { type: Type.NUMBER },
          lineToSite: { type: Type.BOOLEAN },
          connectionType: { type: Type.STRING },
          notes: { type: Type.STRING },
          // Attribution — lifted into `provenance` by the normaliser.
          statedBy: { type: Type.STRING },
          statedByRole: { type: Type.STRING },
          organisation: { type: Type.STRING },
          statedOn: { type: Type.STRING },
          appliesToProject: { type: Type.STRING },
        },
      },
      // REF-LINEAGE REF-1 — optional lineage.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
  // Target docs: state nuclear-stance letters, permitting-agency
  // correspondence, public-policy briefs. Field names mirror
  // `RegulatoryEvidence` (`siteEvidence.ts:303-307`).
  regulatory: {
    type: Type.OBJECT,
    properties: {
      state: { type: Type.STRING },
      // Trichotomy that maps to the rendered chip.
      nuclearStance: { type: Type.STRING },           // 'Favorable' | 'Neutral' | 'Restrictive'
      // Free-form notes — what the doc actually says about the stance.
      stateNotes: { type: Type.STRING },
    },
  },
  // Target docs: geotech reports, site investigation summaries, ASCE 7
  // / IBC design memos. Field names mirror `SeismicEvidence`
  // (`siteEvidence.ts:187-194`). NOTE: the interface field is `pga`,
  // not `pgaG` — match it exactly so the renderer reads what the
  // extractor writes.
  seismic: {
    type: Type.OBJECT,
    properties: {
      // Peak Ground Acceleration in g.
      pga: { type: Type.NUMBER },
      // Design short-period response, g.
      sds: { type: Type.NUMBER },
      // Design 1-second response, g.
      sd1: { type: Type.NUMBER },
      // ASCE 7 Seismic Design Category (A-F).
      designCategory: { type: Type.STRING },
      // Risk Category (I-IV).
      riskCategory: { type: Type.STRING },
      // Site Class (A-F per ASCE 7 Chapter 20).
      siteClass: { type: Type.STRING },
    },
  },
  // ── DAF-19 Wave B: mid-value schemas ─────────────────────────────
  // Target docs: utility rate sheets, retail tariff PDFs, EIA state
  // electricity profiles. Field names mirror `ElectricityEvidence`
  // (`siteEvidence.ts:272-276`). NOTE: `retailRate` is ¢/kWh (cents)
  // not $/kWh — the field name carries the unit; Gemini's
  // structured-output handler is reliable at unit inference from the
  // schema declaration when this is documented.
  electricity: {
    type: Type.OBJECT,
    properties: {
      // Retail rate in ¢/kWh (cents per kWh, not dollars).
      retailRate: { type: Type.NUMBER },
      // Two-letter US state code or full state name (renderer tolerates
      // both). EIA docs typically use the full name.
      state: { type: Type.STRING },
      // Year the cited rate applies to.
      year: { type: Type.NUMBER },
    },
  },
  // Target docs: AirNow regional reports, EPA AQS summaries, state
  // air-board annual reports. Field names mirror `AirQualityEvidence`
  // (`siteEvidence.ts:279-284`).
  airQuality: {
    type: Type.OBJECT,
    properties: {
      // Air Quality Index (0-500).
      aqi: { type: Type.NUMBER },
      // Category label: 'Good' | 'Moderate' | 'Unhealthy for Sensitive
      // Groups' | 'Unhealthy' | 'Very Unhealthy' | 'Hazardous'.
      category: { type: Type.STRING },
      // Pollutant driving the index (e.g. 'PM2.5', 'O3', 'NO2').
      primaryPollutant: { type: Type.STRING },
      // PM2.5 concentration in µg/m³ when reported separately.
      pm25Conc: { type: Type.NUMBER },
    },
  },
  // Target docs: NRC public meeting notices, plant decommissioning
  // notices, site-suitability studies referencing nearby reactors.
  // Field names mirror `NuclearInfraEvidence` (`siteEvidence.ts:249-256`).
  // Gemini reliably converts miles → km when the destination field
  // name explicitly carries the unit (here `nearestReactorDistKm`).
  nuclearInfra: {
    type: Type.OBJECT,
    properties: {
      nearestReactorName: { type: Type.STRING },
      // Distance in kilometres.
      nearestReactorDistKm: { type: Type.NUMBER },
      // Plant rated electrical power in MWe.
      nearestReactorPower: { type: Type.NUMBER },
      // Count of reactors within 200 km when the doc surveys the region.
      reactorsWithin200km: { type: Type.NUMBER },
      // Maturity tag — usually inferable from regional-fleet language.
      nuclearInfraMaturity: { type: Type.STRING },    // 'High' | 'Moderate' | 'Low'
    },
  },
  // ── DAF-19 Wave C: medium-traffic schemas ────────────────────────
  // Target docs: NREL NSRDB summaries, project pre-feasibility studies,
  // solar-resource reports. Field names mirror `SolarEvidence`
  // (`siteEvidence.ts:259-262`). Units carried by field-name suffix.
  solar: {
    type: Type.OBJECT,
    properties: {
      // Global Horizontal Irradiance in kWh/m²/day (daily average).
      ghi: { type: Type.NUMBER },
      // Direct Normal Irradiance in kWh/m²/day.
      dni: { type: Type.NUMBER },
    },
  },
  // Target docs: NREL WIND Toolkit summaries, wind-resource
  // assessments, AWS Truepower studies. Field names mirror
  // `WindEvidence` (`siteEvidence.ts:265-269`). Hub heights in field
  // names; speeds in m/s; capacity factor as fraction 0-1.
  wind: {
    type: Type.OBJECT,
    properties: {
      // 100 m hub-height annual average wind speed, m/s.
      windSpeed100m: { type: Type.NUMBER },
      // 80 m hub-height annual average wind speed, m/s.
      windSpeed80m: { type: Type.NUMBER },
      // Capacity factor as a fraction (0-1).
      capacityFactor: { type: Type.NUMBER },
    },
  },
  // Target docs: fiber-provider quotes, Internet Exchange directories,
  // telecom-infrastructure surveys. Field names mirror
  // `ConnectivityEvidence` (`siteEvidence.ts:287-292`).
  connectivity: {
    type: Type.OBJECT,
    properties: {
      // Nearest Internet Exchange — common names: "Equinix DA1",
      // "DE-CIX NY".
      nearestIxName: { type: Type.STRING },
      // Distance to nearest IX in kilometres.
      nearestIxDistKm: { type: Type.NUMBER },
      // Number of IX facilities within 50 km.
      ixCount50km: { type: Type.NUMBER },
      // Count of colocation / data-centre facilities within 50 km.
      facilityCount50km: { type: Type.NUMBER },
      // D10 — distance to nearest long-haul fiber in kilometres. The
      // range/locus validation for this key existed all along; the
      // schema never ASKED for it, so the model could never return it.
      nearestFiberKm: { type: Type.NUMBER },
      fiberBuildDistanceFt: {
        type: Type.NUMBER,
        description: 'Explicit source-stated last-mile fiber build/construction distance to a service handoff, in feet. Convert explicitly stated miles to feet. Do not use nearest-corridor proximity, IX distance, or a full drawn route; omit when unknown.',
      },
    },
  },
  parcel: {
    type: Type.OBJECT,
    properties: {
      zoning: { type: Type.STRING },
      landUse: { type: Type.STRING },
      owner: { type: Type.STRING },
      acres: { type: Type.NUMBER },
      // REF-LINEAGE REF-1 — optional lineage.
      pageNumber: { type: Type.NUMBER },
      sectionHeading: { type: Type.STRING },
      excerptPreview: { type: Type.STRING },
      ...ATTRIBUTION_SCHEMA_PROPS,
    },
  },
};

/**
 * SED D0.3 — the schema-PROPERTY field names per section (excluding the
 * REF-LINEAGE lineage triple, which is metadata, not section data).
 * Used by the "zero schema-property fields extracted" rejection check
 * so a payload that carries ONLY lineage / unknown keys is treated as
 * an empty extraction rather than a (lineage-only) success.
 */
const SECTION_DATA_FIELDS: Partial<Record<SedSectionId, ReadonlyArray<string>>> = (() => {
  const LINEAGE_KEYS = new Set<string>(['pageNumber', 'sectionHeading', 'excerptPreview', ...ATTRIBUTION_KEYS]);
  const out: Partial<Record<SedSectionId, string[]>> = {};
  for (const [section, schema] of Object.entries(SECTION_SCHEMAS)) {
    const props = (schema as { properties?: Record<string, unknown> } | undefined)?.properties;
    if (!props) continue;
    out[section as SedSectionId] = Object.keys(props).filter(k => !LINEAGE_KEYS.has(k));
  }
  return out;
})();

/**
 * QP-2 — the schema-property (non-lineage) field names for a section.
 * Exported for the bootstrap's multi-doc fill-gaps merge, which needs to
 * know which fields the primary doc left empty before spending another
 * extraction call. Empty array for sections without a channel-A schema.
 */
export function sectionSchemaFieldNames(section: SedSectionId): ReadonlyArray<string> {
  return SECTION_DATA_FIELDS[section] ?? [];
}

/** SED D0.3 — count the schema-property (non-lineage) fields a payload
 *  carries with a non-nullish value. Drives the zero-fields rejection. */
function countExtractedDataFields(section: SedSectionId, data: Record<string, unknown>): number {
  const fields = SECTION_DATA_FIELDS[section];
  if (!fields) return 0;
  let n = 0;
  for (const f of fields) {
    if (!(f in data)) continue;
    const v = data[f];
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    n += 1;
  }
  return n;
}

/**
 * H-4 (passage-offset citation deep-links) — locate a verbatim excerpt
 * within a document's extracted text and return its character span
 * `[start, end)`, or `undefined` when the excerpt does NOT occur verbatim.
 *
 * This is the PRODUCER side of the passage-level provenance: it converts
 * the model-emitted `excerptPreview` (REF-1, a ≤140-char verbatim quote)
 * into a deterministic offset into `SiteDocument.textContent`, which is
 * exactly the text the doc viewer / extracted-text panel renders. The
 * resulting offset lets a `[Ref N]` deep-link highlight the precise
 * passage instead of just opening the page.
 *
 * ZERO-FABRICATION CONTRACT (CLAUDE.md §1/§5, the "zero unverified" North
 * Star): we ONLY return an offset on a real match. Resolution order:
 *   1. Exact `indexOf` of the trimmed excerpt — the common case when
 *      Gemini honoured the "verbatim quote" instruction.
 *   2. A whitespace-normalised match — the model frequently collapses
 *      runs of spaces / newlines (PDF line wraps) in its excerpt. We
 *      rebuild the span against the ORIGINAL text indices so the returned
 *      offset always indexes the real `textContent`, never the normalised
 *      copy. We require the normalised excerpt to be a contiguous token
 *      run in the normalised text so we never stitch together a span that
 *      isn't actually present.
 *   3. Otherwise `undefined` — the excerpt was paraphrased / not present.
 *      We NEVER guess an offset; the caller falls back to page → filename.
 *
 * Pure / synchronous / no I/O. Exported for the regression suite.
 *
 * @param text    The document's extracted `textContent`.
 * @param excerpt The verbatim excerpt to locate (already ≤140 chars).
 */
export function locateExcerptOffset(
  text: string | undefined | null,
  excerpt: string | undefined | null,
): PassageOffset | undefined {
  if (typeof text !== 'string' || typeof excerpt !== 'string') return undefined;
  const needle = excerpt.trim();
  // Require a non-trivial excerpt — a 1-2 char "match" is noise, not a
  // citable passage, and would highlight an arbitrary letter.
  if (needle.length < 8 || text.length === 0) return undefined;

  // 1. Exact verbatim match.
  const exact = text.indexOf(needle);
  if (exact >= 0) return { start: exact, end: exact + needle.length };

  // 2. Whitespace-normalised match, mapped back to ORIGINAL indices.
  //    Build the normalised text alongside an index map (normalised char
  //    position → original char position) so the recovered span indexes
  //    the real `text`, not the normalised copy. Collapsing rule: every
  //    maximal run of ASCII/Unicode whitespace becomes a single space.
  const normChars: string[] = [];
  const normToOrig: number[] = [];
  let prevWasSpace = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (/\s/.test(ch)) {
      if (prevWasSpace) continue; // collapse the run
      normChars.push(' ');
      normToOrig.push(i);
      prevWasSpace = true;
    } else {
      normChars.push(ch);
      normToOrig.push(i);
      prevWasSpace = false;
    }
  }
  const normText = normChars.join('');
  const normNeedle = needle.replace(/\s+/g, ' ');
  if (normNeedle.length < 8) return undefined;

  const normHit = normText.indexOf(normNeedle);
  if (normHit < 0) return undefined;

  // Map the normalised span back to original indices. `start` is the
  // original index of the first normalised char; `end` is one PAST the
  // original index of the last normalised char (half-open `[start, end)`).
  const start = normToOrig[normHit]!;
  const lastNormIdx = normHit + normNeedle.length - 1;
  const lastOrigIdx = normToOrig[lastNormIdx]!;
  const end = lastOrigIdx + 1;
  if (!(end > start)) return undefined;
  return { start, end };
}

/* ── EX-2: DOC-channel validation ─────────────────────────────────────
 *
 * The doc channel pulled Gemini's numbers straight into the SED with NO
 * range / unit / plausibility check — the single channel missing the
 * machinery the site-context channel already has. That is the source of
 * most of the ~70 wrong values (load-factor unit/scale slips, wrong-field
 * acreage, implausible fiber distances, ...).
 *
 * `SECTION_FIELD_RANGES` maps a section's Gemini-response field name to a
 * canonical FIELD_SCHEMAS path. `validateSectionData` then runs the
 * shared `validateValue` primitive over each extracted field and DELETES
 * any field that fails (out-of-range / wrong-type / wrong-unit). Deleting
 * — rather than overwriting with a guessed value — marks the slot
 * UNFILLED so the bootstrap leaves it for re-seeking, never writes a
 * wrong value (CLAUDE.md §1, §5). A telemetry breadcrumb is returned for
 * the audit trail (mirrors `crossStateRejection`).
 *
 * NOTE the distinct name: `SECTION_SCHEMAS` (above) is the Gemini RESPONSE
 * schema; `SECTION_FIELD_RANGES` is the VALIDATION table — and avoids the
 * pre-existing `SECTION_SCHEMAS` export in `config/sectionSchemas.ts`.
 *
 * Only fields with a meaningful physical range are listed. String / enum
 * fields (e.g. `floodZone`, `nuclearStance`) and the `state` field
 * (handled by the cross-state guard) are intentionally absent.
 */
const SECTION_FIELD_RANGES: Partial<Record<SedSectionId, Readonly<Record<string, string>>>> = {
  climate: {
    avgTemp: 'climate.avgTemp',
    peakTemp: 'climate.peakTemp',
    avgWetBulb: 'climate.avgWetBulb',
    humidity: 'climate.humidity',
  },
  grid: {
    substationDistKm: 'grid.substationDistKm',
  },
  parcel: {
    acres: 'parcel.acres',
  },
  environmental: {
    wetlandAcres: 'environmental.wetlandAcres',
  },
  seismic: {
    pga: 'seismic.pga',
  },
  electricity: {
    retailRate: 'electricity.retailRate',
  },
  airQuality: {
    aqi: 'airQuality.aqi',
  },
  solar: {
    ghi: 'solar.ghi',
  },
  connectivity: {
    nearestIxDistKm: 'connectivity.nearestIxDistKm',
    nearestFiberKm: 'connectivity.nearestFiberKm',
    fiberBuildDistanceFt: 'connectivity.fiberBuildDistanceFt',
  },
  nuclearInfra: {
    nearestReactorDistKm: 'nuclearInfra.nearestReactorDistKm',
  },
  // NOTE: `wind.capacityFactor` is deliberately NOT locus-checked here.
  // The doc schema stores it as a FRACTION (0-1) while docs report a
  // PERCENTAGE ("38%"), so the stored 0.38 would never token-match the
  // "38" near the "capacity factor" keyword — a locus check would
  // false-reject a correct unit conversion. The [0,1] range guard already
  // catches the 5.5↔83.3 load-factor swap (both are out of [0,1]). The
  // percentage-scale load factor (`capacityFactorPct`) IS locus-checked in
  // FIELD_SCHEMAS for the requirement-doc channel, where both 5.5 and 83.3
  // are in [0,100] and only the locus check can tell them apart.
};

/**
 * EX-6 — doc-channel boolean fields that may carry an inverted polarity.
 * Maps a section to its boolean fields, each with the PREDICATE KEYWORDS
 * that identify a sentence about that specific field. We only flip a
 * field when (a) the model said `true`, AND (b) the co-located excerpt
 * carries an unambiguous negation marker, AND (c) the excerpt mentions
 * one of THIS field's predicate keywords. Requirement (c) prevents a
 * section-wide excerpt with a single negation from wrongly flipping
 * EVERY boolean in the section (e.g. a "no wetlands" sentence must not
 * flip `hasCriticalHabitat`). Conservative: never flip `false`→`true`.
 */
interface DocBooleanField {
  readonly field: string;
  readonly keywords: readonly string[];
}
const SECTION_BOOLEAN_FIELDS: Partial<Record<SedSectionId, readonly DocBooleanField[]>> = {
  flood: [
    { field: 'sfha', keywords: ['sfha', 'special flood hazard', 'flood hazard area', 'flood zone'] },
  ],
  environmental: [
    { field: 'hasWetlands', keywords: ['wetland', 'wetlands'] },
    { field: 'hasCriticalHabitat', keywords: ['critical habitat', 'habitat', 'endangered', 'threatened species'] },
  ],
};

/** EX-2 telemetry breadcrumb for a rejected (deleted) doc-channel field. */
export interface DocFieldRejection {
  field: string;
  canonicalPath: string;
  rejected: string | number | boolean;
  reasons: string[];
}

/**
 * EX-2 / EX-4 — validate the extracted `data` against
 * `SECTION_FIELD_RANGES` using the shared `validateValue` primitive.
 * MUTATES `data`: deletes any field that fails so the slot is left
 * UNFILLED (not silently overwritten). Returns the list of rejections for
 * telemetry.
 *
 * Conservative by construction: a field with no range mapping, or a
 * `null` / `undefined` value, is left untouched (validateValue returns
 * `ok` for unknown paths + nullish values). The DOC channel passes NO
 * `sourceSpan`/`unit`, so the unit-token guard is intentionally skipped
 * (Gemini writes to a typed slot, not citable prose) — only the
 * type/range/enum checks run.
 *
 * EX-4 — when `locusSpan` is supplied (the doc body / excerpt / summary),
 * the locus-keyword check ALSO runs for numeric fields that declare
 * `locusKeywords`. That is the only check that catches an IN-RANGE wrong
 * value (load-factor 5.5↔83.3, acreage 332↔800): the extracted number
 * must appear near its field keyword in the source prose, else the field
 * is rejected (slot left unfilled). Omitting `locusSpan` reproduces the
 * pre-EX-4 behaviour exactly. Pure except for the documented `data`
 * mutation.
 */
export function validateSectionData(
  section: SedSectionId,
  data: Record<string, unknown>,
  locusSpan?: string,
): DocFieldRejection[] {
  const ranges = SECTION_FIELD_RANGES[section];
  if (!ranges) return [];
  const rejections: DocFieldRejection[] = [];
  for (const [field, canonicalPath] of Object.entries(ranges)) {
    if (!(field in data)) continue;
    const value = data[field];
    if (value === null || value === undefined) continue;
    // Only string | number | boolean are validatable scalars; arrays /
    // objects are out of scope for the range/type guard.
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      continue;
    }
    const r = validateValue(canonicalPath, value, locusSpan !== undefined ? { locusSpan } : undefined);
    if (!r.ok) {
      rejections.push({
        field,
        canonicalPath,
        rejected: value,
        reasons: r.failures.map(f => f.reason),
      });
      // Reject = leave the slot unfilled. NEVER write a wrong value.
      delete data[field];
    }
  }
  return rejections;
}

/**
 * EX-6 — apply the conservative negation-polarity flip to the doc
 * channel's boolean fields. MUTATES `data`. Flips a boolean from `true`
 * to `false` ONLY when ALL of:
 *   (a) the model emitted `true` for the field, AND
 *   (b) the co-located excerpt carries an unambiguous negation marker
 *       (`detectNegationPolarity(excerpt) === true`), AND
 *   (c) the excerpt mentions one of THIS field's predicate keywords
 *       (so a "no wetlands" sentence flips `hasWetlands`, never
 *       `hasCriticalHabitat`).
 * We NEVER flip `false`→`true`: a missing negation marker is "no signal".
 *
 * The site-context channel's polarity logic answers a different question
 * ("is the CLAIM about a negated predicate?"), so it flips toward `true`.
 * Here the field is a FIXED predicate (e.g. `flood.sfha` = "is in an
 * SFHA"); a negation of that predicate means the field is FALSE. Returns
 * the names of fields it flipped.
 */
export function applyDocBooleanNegation(
  section: SedSectionId,
  data: Record<string, unknown>,
  excerpt: string,
  wetlandSource: string = excerpt,
): string[] {
  const fields = SECTION_BOOLEAN_FIELDS[section];
  if (!fields || !excerpt) return [];
  const negated = detectNegationPolarity(excerpt) === true;
  const lower = excerpt.toLowerCase();
  const flipped: string[] = [];
  for (const { field, keywords } of fields) {
    if (data[field] !== true) continue;
    if (field === 'hasWetlands') {
      if (explicitWetlandPresence(wetlandSource) !== false) continue;
    } else {
      if (!negated || !keywords.some(k => lower.includes(k))) continue;
    }
    data[field] = false;
    flipped.push(field);
  }
  return flipped;
}

export interface ExtractFromDocumentInput {
  /** Attempt-owned cancellation/watchdog signal. */
  abortSignal?: AbortSignal;
  doc: SiteDocument;
  section: SedSectionId;
  /** Override for tests; defaults to runGeminiJSON. */
  runner?: typeof runGeminiJSON;
  /**
   * DAT-FOLLOWUP-1 — site-bound state prior. When supplied, the
   * extractor will:
   *   1. Inject a locus-binding instruction into the prompt for
   *      sections whose schema carries a `state` field (e.g.
   *      `regulatory`, `electricity`) — telling Gemini to only
   *      populate `state` when the doc explicitly attributes the SITE
   *      (not just any reference) to that state.
   *   2. Post-hoc, when the extracted `state` disagrees with the site
   *      prior AND no locus-binding phrase is detectable in the doc
   *      text, scrub the `state` field from `data` so the downstream
   *      merger does not silently overwrite the provider / site-bound
   *      value. The result still ships — other fields in the schema
   *      may have valid contributions.
   *
   * Two-letter USPS code (uppercased). Pass `null` / `undefined` to
   * disable the guard entirely (legacy behaviour). Callers that don't
   * know the site state SHOULD pass `null` rather than guess —
   * guessing reintroduces the very bug DAT-FOLLOWUP-1 closes.
   */
  siteState?: string | null;
  /**
   * DAT-FOLLOWUP-1 — site name for locus-binding detection.
   * `hasLocusBindingPhrase` widens its accepted patterns when a site
   * name is supplied (e.g. `<site name> in <state>`). Optional;
   * absent ⇒ only the generic locus subjects (site / facility /
   * plant / parcel / project / property) are matched.
   */
  siteName?: string;
}

export interface ExtractFromDocumentResult {
  data: Record<string, unknown>;
  confidence: number;
  source: string;
  /**
   * REF-LINEAGE REF-1 — 1-based page number the extracted facts were
   * sourced from. Optional; absent when the doc has no detectable
   * page structure (TXT, single-page PDF) or Gemini could not
   * confidently identify a page. Drives the PDF viewer's
   * `initialPage` prop when a `[Ref N]` pill is clicked (REF-3).
   * Fabricated values are silently dropped at projection time
   * (non-positive numbers, strings, NaN → undefined).
   */
  pageNumber?: number;
  /**
   * REF-LINEAGE REF-1 — most-specific heading enclosing the extracted
   * facts. Optional for the same reasons as `pageNumber`. Capped at
   * 120 chars at projection time.
   */
  sectionHeading?: string;
  /**
   * REF-LINEAGE REF-1 — short verbatim quote (≤140 chars) from the
   * doc that the extracted values were derived from. Propagated by
   * REF-2 as `excerptPreview` onto `LinkedDocumentRef`; rendered as
   * an italic snippet under the appendix row (REF-4). Optional.
   */
  excerptPreview?: string;
  /**
   * P4 — who stated the extracted facts, in what role, on what date and for
   * which project, as the document itself says. Projected off `data` like
   * the lineage triple; carried onto the linked-doc ref and rendered in the
   * SED prompt so the report attributes the figure instead of adopting it.
   */
  attribution?: EvidenceAttribution;
  /**
   * H-4 (passage-offset citation deep-links) — character span
   * `[start, end)` of `excerptPreview` within the source doc's
   * `textContent`, when (and ONLY when) the excerpt is found VERBATIM in
   * that text. Lets a `[Ref N]` deep-link scroll to / highlight the exact
   * passage rather than the top of a page (finer than `pageNumber`).
   *
   * NEVER fabricated: computed deterministically by
   * `locateExcerptOffset(textContent, excerptPreview)` — a string search,
   * not a model output — so it exists only when the excerpt genuinely
   * occurs in the doc text the user can inspect. When the model
   * paraphrased the excerpt (no verbatim match) or emitted no excerpt,
   * this is absent and the deep-link resolver falls back to page →
   * filename (zero-unverified North Star).
   *
   * Optional / additive: absent on every doc extracted before H-4, on
   * non-verbatim excerpts, and whenever `excerptPreview` itself is absent.
   */
  sourceOffset?: PassageOffset;
  /**
   * DAT-FOLLOWUP-1 — telemetry breadcrumb. Stamped when the
   * cross-state guard scrubbed a `state` field from the result
   * because the doc-extracted value disagreed with `siteState` AND
   * no locus-binding phrase was detectable in the doc text.
   *
   * Shape: `{ field: 'state', rejected: 'TN', siteState: 'LA' }`.
   * Absent on the happy path (no scrub) so existing consumers stay
   * structurally unchanged. The downstream conflict pass reads this
   * field to surface a manual-review row for the audit trail.
   */
  crossStateRejection?: {
    field: 'state';
    rejected: string;
    siteState: string;
  };
  /**
   * EX-2 — telemetry breadcrumbs for doc-channel fields rejected by the
   * range/unit/plausibility guard (and consequently DELETED from
   * `data`, i.e. the slot is left unfilled). Absent on the happy path
   * so existing consumers stay structurally unchanged. Lets the audit
   * trail / admin rollup show "rejected acres=500000 (out-of-range)".
   */
  fieldRejections?: DocFieldRejection[];
  /**
   * EX-6 — names of boolean fields whose polarity was flipped
   * (model `true` → `false`) because the co-located excerpt carried an
   * unambiguous negation of the field's predicate. Absent when nothing
   * was flipped.
   */
  booleanFlips?: string[];
}

/**
 * SED D0.3 — structured rejection reason for an extraction that was
 * ATTEMPTED but produced no usable data. Distinguishes the previously
 * silent `return null` drops so the caller can stamp a non-silent
 * `extractionAttempt` on the slot ("matched doc, attempted, rejected:
 * <reason>") rather than the slot vanishing with no explanation.
 *
 *  - `'no-schema'`              — the section has no extraction schema.
 *  - `'empty-text'`            — doc text under the 50-char guard.
 *  - `'binary'`                — doc text is a binary/image/geo/video
 *                                placeholder.
 *  - `'call-failed'`          — the Gemini call threw / timed out /
 *                                returned malformed JSON.
 *  - `'all-required-missing'` — the call returned but ZERO
 *                                schema-property fields survived (no
 *                                section data at all). Named for
 *                                continuity with the dropped `required`
 *                                arrays even though they no longer gate.
 *  - `'all-fields-out-of-range'` — fields WERE extracted but every one
 *                                was deleted by the EX-2/EX-4
 *                                range/locus validator (out-of-range /
 *                                wrong-locus), leaving the payload empty.
 *  - `'cross-state'`          — the only extracted field was a
 *                                cross-attributed `state` the DAT guard
 *                                scrubbed, leaving the payload empty.
 */
export type ExtractionRejectionReason =
  | 'no-schema'
  | 'empty-text'
  | 'binary'
  | 'call-failed'
  | 'all-required-missing'
  | 'all-fields-out-of-range'
  | 'cross-state';

export interface ExtractionRejection {
  rejected: true;
  reason: ExtractionRejectionReason;
  /** EX-2 breadcrumbs carried through when the rejection was caused by
   *  the range/locus validator deleting every field. */
  fieldRejections?: DocFieldRejection[];
  /** DAT-FOLLOWUP-1 breadcrumb carried through on a `'cross-state'`
   *  rejection. */
  crossStateRejection?: ExtractFromDocumentResult['crossStateRejection'];
  /** Optional human-readable detail (caught-exception message, etc.). */
  detail?: string;
}

/** SED D0.3 — discriminated outcome of an extraction attempt. The
 *  success arm is the legacy `ExtractFromDocumentResult`; the rejection
 *  arm carries the reason + any per-field breadcrumbs. */
export type ExtractFromDocumentOutcome = ExtractFromDocumentResult | ExtractionRejection;

/** SED D0.3 — type guard. True for the rejection arm of an outcome. */
export function isExtractionRejection(
  outcome: ExtractFromDocumentOutcome | null | undefined,
): outcome is ExtractionRejection {
  return !!outcome && (outcome as ExtractionRejection).rejected === true;
}

/**
 * SED D0.3 — discriminated-outcome worker behind `extractFromDocument`.
 * Run Gemini against the doc's textContent + summary to pull out the
 * shape required by the SED section. Returns a success
 * `ExtractFromDocumentResult` OR a structured `ExtractionRejection`
 * carrying the reason (no-schema / empty-text / binary / call-failed /
 * all-required-missing / all-fields-out-of-range / cross-state) so the
 * caller can record a non-silent `extractionAttempt` on the slot.
 *
 * `extractFromDocument` is the back-compat adapter that collapses the
 * rejection arm to `null` for the (many) callers that only care about
 * success-or-not.
 */
export async function extractFromDocumentDetailed(
  input: ExtractFromDocumentInput,
): Promise<ExtractFromDocumentOutcome> {
  throwIfAborted(input.abortSignal);
  const schema = SECTION_SCHEMAS[input.section];
  if (!schema) return { rejected: true, reason: 'no-schema' };

  // P1.4 — the whole body when captured (`fullExtractedText`), else the preview.
  const text = documentBodyText(input.doc).trim();
  if (text.length < 50) return { rejected: true, reason: 'empty-text' };
  if (text.startsWith('[Binary file') || text.startsWith('[Image') || text.startsWith('[Geographic') || text.startsWith('[Video')) {
    return { rejected: true, reason: 'binary' };
  }
  return extractFromDocumentCore(input, schema, text);
}

/**
 * Back-compat adapter — collapses the SED-D0.3 discriminated outcome to
 * `ExtractFromDocumentResult | null`. EVERY pre-D0.3 caller
 * (`memoisedExtractFromDocument`, the resolver, the report formatters,
 * …) reads this null contract; keeping it intact bounds the blast
 * radius of the recall change. Callers that need the rejection reason
 * (the bootstrap doc-aware loop) use `extractFromDocumentDetailed`.
 */
export async function extractFromDocument(
  input: ExtractFromDocumentInput,
): Promise<ExtractFromDocumentResult | null> {
  const outcome = await extractFromDocumentDetailed(input);
  return isExtractionRejection(outcome) ? null : outcome;
}

/**
 * SED D0.3 — the Gemini-call core, factored out of `extractFromDocument`
 * so the schema / empty-text / binary pre-checks live in
 * `extractFromDocumentDetailed` (which can return their structured
 * reasons). `schema` + `text` are already validated by the caller.
 * Returns a success result OR a structured rejection
 * (`call-failed` / `all-required-missing` / `all-fields-out-of-range` /
 * `cross-state`). Behaviour on the SUCCESS path is byte-for-byte the
 * pre-D0.3 path; only the previously-silent `return null` drops became
 * structured rejections.
 */
async function extractFromDocumentCore(
  input: ExtractFromDocumentInput,
  schema: Record<string, unknown>,
  text: string,
): Promise<ExtractFromDocumentOutcome> {
  const runner = input.runner ?? runGeminiJSON;
  // URE-2 — surface user intent to the extractor when the doc was
  // explicitly uploaded for a requirement that maps to this section.
  // The hint tells Gemini "this is the right doc for this section"
  // so it commits to the extraction rather than hedging when the
  // text looks borderline.
  const intentBlock = (() => {
    const intents = input.doc.intendedRequirementIds ?? [];
    if (intents.length === 0) return '';
    const relevant = intents.filter(reqId => {
      const sections = REQUIREMENT_TO_SECTION_MAP[reqId];
      return sections && sections.includes(input.section);
    });
    if (relevant.length === 0) return '';
    return `USER INTENT: This document was explicitly uploaded by the user to fulfill ` +
      `requirement(s) ${relevant.join(', ')}, which directly cover the ${input.section} ` +
      `SED section. Extract every available field in the schema even when phrasing is informal.\n\n`;
  })();
  // DAT-FOLLOWUP-1 — locus-binding instruction. Tells Gemini to only
  // populate `state` when the doc explicitly attributes the SITE
  // (not a stray reference) to that state. Suppresses the live-prod
  // bug where a Title-chain doc's "Tennessee Gas Pipeline" easement
  // stamped `regulatory.state = "Tennessee"` on a Louisiana site.
  // Only attached when the section schema carries a `state` field AND
  // the caller provided a non-null `siteState` prior.
  const SCHEMA_HAS_STATE_FIELD = input.section === 'regulatory'
    || input.section === 'electricity';
  const siteStateNorm = normaliseStateToCode(input.siteState ?? null);
  const locusBindingBlock = (() => {
    if (!SCHEMA_HAS_STATE_FIELD) return '';
    if (!siteStateNorm) {
      // No prior — fall back to the generic locus-binding instruction.
      return `LOCUS BINDING (state field): only populate the "state" field when the document ` +
        `explicitly attributes THE SITE to that state (e.g. "the site is located in X", ` +
        `"<site name> in X", "<X>-based facility"). Mentions of other states elsewhere in ` +
        `the document — gas pipeline easements, parent company HQ, precedent jurisdictions, ` +
        `title-chain references to neighboring states — MUST NOT populate "state". When in ` +
        `doubt, omit the field.\n\n`;
    }
    return `LOCUS BINDING (state field): the site's known state is ${siteStateNorm}. Only ` +
      `populate "state" with a value OTHER than ${siteStateNorm} when the document explicitly ` +
      `attributes THE SITE to that other state via a clear locus-binding phrase (e.g. "the site ` +
      `is located in X", "<site name> in X", "<X>-based facility"). Mentions of other states ` +
      `elsewhere in the document — gas pipeline easements, parent company HQ, precedent ` +
      `jurisdictions, title-chain references to neighboring states — MUST NOT populate ` +
      `"state". When in doubt, omit the field and let the site-bound value stand.\n\n`;
  })();
  // P1.4 — the head window is extracted first; later windows of a long
  // document get a bounded second pass below.
  const headWindow = text.slice(0, SECTION_EXTRACTION_TEXT_WINDOW);
  const contents =
    `Extract the ${input.section} section data from the document below.\n` +
    `Return JSON matching the supplied schema. If a field cannot be confidently inferred, omit it ` +
    `(do not guess). The document was uploaded by the user and may be partial — extracting what's ` +
    `present is better than fabricating.\n\n` +
    intentBlock +
    locusBindingBlock +
    // REF-LINEAGE REF-1 — page + heading + excerpt lineage. Each is
    // OPTIONAL: emit only when you can identify the source page /
    // heading / quote with high confidence. Fabricating a page number
    // is worse than omitting it — page numbers feed a deep-link the
    // user will click to verify.
    `For lineage: when the document has clear page boundaries (PDF, paginated DOCX), ` +
    `also return:\n` +
    `  - pageNumber: the 1-based page where the facts above appear.\n` +
    `  - sectionHeading: the most-specific heading enclosing those facts (≤120 chars).\n` +
    `  - excerptPreview: a verbatim quote (≤140 chars) of the passage the facts came from.\n` +
    `Omit any lineage field you cannot verify against the document text.\n\n` +
    `${ATTRIBUTION_PROMPT}\n\n` +
    `DOCUMENT FILENAME: ${input.doc.fileName}\n` +
    `SUMMARY: ${(input.doc.summary ?? '').slice(0, 500)}\n` +
    `BODY:\n${headWindow}`;

  try {
    const data = await runner<Record<string, unknown>>({
      // Cost lever (strategy §4): extraction is structured field-pull, not
      // judgment — route to Flash when tiering is on, Pro when off.
      model: tieredModelFor('extraction'),
      contents,
      schema: schema as never,
      timeoutMs: DOC_EXTRACTION_TIMEOUT_MS,
      signal: input.abortSignal,
    });
    throwIfAborted(input.abortSignal);
    // REF-LINEAGE REF-1 — project the lineage fields off `data` and
    // onto the result. Downstream schema validators (SED renderers,
    // DOCX exporter, conflict detector) iterate `slot.data` keys; a
    // surprise `pageNumber`/`sectionHeading`/`excerptPreview` mixed
    // into `ClimateEvidence` etc. would force every consumer to widen
    // its type. Project them out and the data payload stays pristine.
    //
    // Defensive parsing:
    //   - pageNumber: positive integer (Math.floor); strings/NaN/0/<0 → undefined.
    //   - sectionHeading: trimmed string; cap at 120 chars; numbers/empty → undefined.
    //   - excerptPreview: trimmed string; cap at 140 chars; numbers/empty → undefined.
    let pageNumber = typeof data.pageNumber === 'number' && Number.isFinite(data.pageNumber) && data.pageNumber > 0
      ? Math.floor(data.pageNumber)
      : undefined;
    let sectionHeading = typeof data.sectionHeading === 'string' && data.sectionHeading.trim()
      ? data.sectionHeading.slice(0, 120)
      : undefined;
    let excerptPreview = typeof data.excerptPreview === 'string' && data.excerptPreview.trim()
      ? data.excerptPreview.slice(0, 140)
      : undefined;
    delete data.pageNumber;
    delete data.sectionHeading;
    delete data.excerptPreview;
    // P4 — attribution travels WITH the facts, not inside them.
    let attribution = takeAttributionFromPayload(data);

    // P6 — per-section payload normalisers. For `waterResource`: shape the
    // nested sub-objects, lift attribution into `provenance`, drop sub-objects
    // with no figure (so the zero-fields rejection still fires) and scrub the
    // contract keys. No-op for every other section.
    sanitizeDocumentSectionFields(input.section, data, headWindow);
    normaliseSectionPayload(input.section, data, { documentId: input.doc.id, documentName: input.doc.fileName });

    // P1.4 — windowed second pass. When the document is longer than the head
    // window and schema fields are still empty, extract from the best later
    // windows (bounded by EXTRA_EXTRACTION_WINDOWS) and fill only what the
    // head pass left empty. The text of every window that contributed joins
    // the locus span below so the range/locus validator can see it.
    let extraWindowsText = '';
    if (text.length > SECTION_EXTRACTION_TEXT_WINDOW) {
      const missingFields = sectionSchemaFieldNames(input.section).filter(f => isMissingExtractedValue(data[f]));
      if (missingFields.length > 0) {
        for (const window of selectExtractionWindows(text, input.section)) {
          throwIfAborted(input.abortSignal);
          try {
            const extra = await runner<Record<string, unknown>>({
              model: tieredModelFor('extraction'),
              contents: contents.replace(
                `BODY:\n${headWindow}`,
                `BODY (a later part of the document, characters ${window.start}-${window.end}):\n${window.text}`,
              ),
              schema: schema as never,
              timeoutMs: DOC_EXTRACTION_TIMEOUT_MS,
              signal: input.abortSignal,
            });
            throwIfAborted(input.abortSignal);
            const extraExcerpt = typeof extra.excerptPreview === 'string' && extra.excerptPreview.trim()
              ? extra.excerptPreview.slice(0, 140)
              : undefined;
            const extraHeading = typeof extra.sectionHeading === 'string' && extra.sectionHeading.trim()
              ? extra.sectionHeading.slice(0, 120)
              : undefined;
            delete extra.pageNumber;
            delete extra.sectionHeading;
            delete extra.excerptPreview;
            const extraAttribution = takeAttributionFromPayload(extra);
            sanitizeDocumentSectionFields(input.section, extra, window.text);
            normaliseSectionPayload(input.section, extra, { documentId: input.doc.id, documentName: input.doc.fileName });
            let filled = 0;
            for (const field of missingFields) {
              if (!isMissingExtractedValue(extra[field]) && isMissingExtractedValue(data[field])) {
                data[field] = extra[field];
                filled += 1;
              }
            }
            if (filled > 0) {
              extraWindowsText += `\n${window.text}`;
              attribution = mergeAttribution(attribution, extraAttribution);
              if (excerptPreview === undefined && extraExcerpt) {
                excerptPreview = extraExcerpt;
                if (sectionHeading === undefined) sectionHeading = extraHeading;
              }
            }
          } catch (e) {
            throwIfAborted(input.abortSignal);
            console.warn(
              `[extractFromDocument] section=${input.section} doc=${input.doc.id} window ${window.start}-${window.end} failed (head-window result kept):`,
              (e as Error)?.message,
            );
          }
        }
      }
    }

    // H-4 (passage-offset citation deep-links) — deterministically locate
    // the verbatim excerpt within the FULL extracted text (not the
    // prompt window, so an excerpt near the doc tail still
    // resolves). Returns an offset ONLY on a real match; a paraphrased /
    // absent excerpt yields `undefined` and the deep-link falls back to
    // page → filename. Never fabricated.
    const sourceOffset = locateExcerptOffset(text, excerptPreview);
    // P1.4 — a verbatim excerpt located in the body gives a RELIABLE page
    // (from the page map) that beats the model's guess.
    const derivedPage = pageForCharOffset(input.doc.pages, sourceOffset?.start);
    if (derivedPage !== undefined) pageNumber = derivedPage;

    // EX-2 — DOC-channel range/unit/plausibility validation. Runs AFTER
    // lineage projection (so lineage keys aren't validated) and BEFORE
    // the cross-state guard + return. Any field that fails the shared
    // `validateValue` primitive is DELETED from `data` (slot left
    // unfilled, never silently overwritten with a wrong value). The
    // rejections are stamped on the result for the audit trail. This is
    // the single chokepoint covering BOTH the unavailable-fill path and
    // the SSM verified-override path, plus the extraction cache (which
    // memoises this function's output verbatim — hence the v4 cache bump).
    // EX-4 — locus span: the same prose Gemini extracted from. Prefer the
    // model-cited excerpt (most specific) but include the summary + body
    // head so a field whose keyword is outside the 140-char excerpt is
    // still verifiable. `valueSupportedNearKeyword` skips fields whose
    // keyword never appears here (conservative), so a larger span only
    // ever INCREASES coverage, never over-rejects.
    const locusSpan = `${excerptPreview ?? ''}\n${(input.doc.summary ?? '').slice(0, 500)}\n${headWindow}${extraWindowsText}`;
    const fieldRejections = validateSectionData(input.section, data, locusSpan);
    if (fieldRejections.length > 0) {
      console.warn(
        `[extractFromDocument] section=${input.section} doc=${input.doc.id} rejected ${fieldRejections.length} out-of-range field(s):`,
        fieldRejections.map(r => `${r.field}=${String(r.rejected)}`).join(', '),
      );
    }

    // EX-6 — DOC-channel boolean negation flip. Uses the verbatim excerpt
    // (captured above before deletion) + the doc body as the co-located
    // context. Conservative: flips only a model `true` to `false` on an
    // unambiguous, field-keyword-co-located negation marker.
    const negationContext = `${excerptPreview ?? ''} ${(input.doc.summary ?? '').slice(0, 500)} ${text.slice(0, 2000)}`;
    // Wetlands require source text: a generated summary cannot supply a missing
    // predicate, and separate windows cannot become one artificial sentence.
    const wetlandSource = `${headWindow}\n${extraWindowsText}`;
    const booleanFlips = applyDocBooleanNegation(input.section, data, negationContext, wetlandSource);
    if (input.section === 'environmental' && typeof data.hasWetlands === 'boolean'
      && explicitWetlandPresence(wetlandSource) !== data.hasWetlands) {
      fieldRejections.push({ field: 'hasWetlands', canonicalPath: 'environmental.hasWetlands',
        rejected: data.hasWetlands, reasons: ['Source does not explicitly establish matching on-site wetland presence or absence.'] });
      delete data.hasWetlands;
    }
    if (booleanFlips.length > 0) {
      console.warn(
        `[extractFromDocument] section=${input.section} doc=${input.doc.id} flipped boolean polarity (negation detected):`,
        booleanFlips.join(', '),
      );
    }

    // DAT-FOLLOWUP-1 — post-hoc cross-state guard. The prompt
    // instruction is best-effort; Gemini still occasionally returns a
    // cross-state value (especially on Title docs with rich
    // out-of-state references). Apply a deterministic check:
    //
    //   1. If the section's schema doesn't carry `state`, no-op.
    //   2. If the caller didn't pass `siteState`, no-op (legacy).
    //   3. If the extracted `state` normalises to the same code as
    //      `siteState`, no-op — happy path, value stays in `data`.
    //   4. If they disagree AND no locus-binding phrase is detectable
    //      in `summary + textContent`, scrub `state` from `data` and
    //      stamp `crossStateRejection` for the audit trail. Other
    //      fields in the schema (e.g. `nuclearStance`, `stateNotes`)
    //      are NOT scrubbed — the doc may have legitimate contributions
    //      on the non-locus fields and a downstream merger will route
    //      them through provider-state context.
    //
    // The locus-binding scan happens AFTER the Gemini call rather than
    // before (which would let us short-circuit to `null`) because
    // hasLocusBindingPhrase scoring is conservative — a false-negative
    // would silently drop legitimate cross-state evidence. Running
    // post-hoc means a cross-state extraction with a real binding
    // phrase still ships; only the genuinely cross-attributed cases
    // get scrubbed.
    let crossStateRejection: ExtractFromDocumentResult['crossStateRejection'];
    if (SCHEMA_HAS_STATE_FIELD && siteStateNorm) {
      const extractedState = data.state;
      if (typeof extractedState === 'string' && extractedState.trim().length > 0) {
        const extractedCode = normaliseStateToCode(extractedState);
        if (extractedCode && extractedCode !== siteStateNorm) {
          const haystack = `${input.doc.summary ?? ''}\n${text}`;
          const locusOk = hasLocusBindingPhrase(haystack, extractedCode, {
            siteName: input.siteName,
          });
          if (!locusOk) {
            crossStateRejection = {
              field: 'state',
              rejected: extractedCode,
              siteState: siteStateNorm,
            };
            // Drop the state field from data; downstream merger will
            // leave the existing provider / site-bound value in place.
            delete data.state;
          }
        }
      }
    }

    // SED D0.3 — zero-fields rejection. Now that the `required` arrays
    // are gone, a PARTIAL payload (a subset of the section's fields) is
    // a legitimate success. But when every schema-property field is
    // absent or was deleted by the EX-2/EX-4 validator + cross-state
    // guard, the payload carries no section data — surface a structured
    // rejection instead of returning an empty-data "success" the slot
    // would read as filled. The reason distinguishes WHY nothing landed
    // so the Coverage Matrix chip can say "matched, attempted, rejected:
    // <reason>".
    if (countExtractedDataFields(input.section, data) === 0) {
      if (fieldRejections.length > 0) {
        // Fields WERE extracted but every one failed the range/locus
        // validator.
        return {
          rejected: true,
          reason: 'all-fields-out-of-range',
          fieldRejections,
          ...(crossStateRejection ? { crossStateRejection } : {}),
        };
      }
      if (crossStateRejection) {
        // The only extracted field was a cross-attributed state we
        // scrubbed.
        return { rejected: true, reason: 'cross-state', crossStateRejection };
      }
      // Gemini returned but with no usable schema-property field.
      return { rejected: true, reason: 'all-required-missing' };
    }

    return {
      data,
      confidence: documentExtractionConfidence(input.doc),
      // MV5: launder the citation source so downstream prose doesn't
      // leak the raw upload filename. Carry the raw filename in
      // parentheses so the upstream citation tracer (which matches on
      // the raw upload filename) still hits — display name for prose,
      // raw name for the tracer.
      source: `Extracted from "${cleanDocFileName(input.doc.fileName)}" (source: ${input.doc.fileName}, uploaded ${new Date(input.doc.uploadedAt).toLocaleDateString()})`,
      pageNumber,
      sectionHeading,
      excerptPreview,
      ...(attribution ? { attribution } : {}),
      // H-4 — only present when the excerpt matched verbatim (else undefined).
      ...(sourceOffset ? { sourceOffset } : {}),
      ...(crossStateRejection ? { crossStateRejection } : {}),
      ...(fieldRejections.length > 0 ? { fieldRejections } : {}),
      ...(booleanFlips.length > 0 ? { booleanFlips } : {}),
    };
  } catch (e) {
    rethrowIfAborted(input.abortSignal, e);
    console.warn(`[extractFromDocument] section=${input.section} doc=${input.doc.id} failed:`, (e as Error)?.message);
    // SED D0.3 — promote the silent `return null` on a thrown / timed-out
    // / malformed-JSON call to a structured `call-failed` rejection so
    // the caller can stamp a non-silent extractionAttempt.
    return { rejected: true, reason: 'call-failed', detail: (e as Error)?.message };
  }
}

/** True when a doc qualifies for fallback into the given section.
 *
 *  URE-2 — `intendedRequirementIds` is a user-stamped explicit
 *  signal stronger than the classifier's `sedSectionsHelpful` /
 *  `relevance` heuristics. When the doc was uploaded FOR a
 *  requirement that maps to this section, it qualifies even if the
 *  classifier disagrees about `sedSectionsHelpful` membership or the
 *  per-report relevance is below threshold. The +0.5 score bias in
 *  `pickBestFallbackDoc` then ensures intent-driven docs beat
 *  generic high-relevance ones at selection time.
 */
export function docQualifiesForFallback(
  doc: SiteDocument,
  section: SedSectionId,
  reportType: string,
  // SED D0.3 — default aligned to the DCT match floor (0.4) so the
  // extraction-attempt gate no longer drops the [0.4, 0.6) "matched
  // but not extracted" band. Was a hard-coded 0.6.
  threshold = EXTRACTION_RELEVANCE_FLOOR,
): boolean {
  // URE-2 — intent fast-path. Bypasses the classifier checks below.
  if (doc.intendedRequirementIds && doc.intendedRequirementIds.length > 0) {
    for (const reqId of doc.intendedRequirementIds) {
      const sections = REQUIREMENT_TO_SECTION_MAP[reqId];
      if (sections && sections.includes(section)) return true;
    }
  }
  if (!doc.sedSectionsHelpful?.includes(section)) return false;
  const score = doc.relevance?.[reportType as never];
  if (typeof score !== 'number') return false;
  return score >= threshold;
}

/**
 * Returns the doc with the highest relevance for the given report
 * type whose `sedSectionsHelpful` includes the target section. Used
 * by the bootstrap pipeline to pick *one* fallback doc per missing
 * section without bloating context.
 *
 * URE-2 — when a doc carries `intendedRequirementIds` AND one of
 * those requirements maps to the target section (via
 * REQUIREMENT_TO_SECTION_MAP), apply a +0.5 score bias so the
 * explicitly-intended doc wins over a higher-relevance but
 * non-intended one. This means a utility letter uploaded FOR
 * `'utility-letter'` always beats a generic doc with 0.7 relevance
 * for the grid section, even when the latter has a higher
 * classifier score. Intent is a stronger signal than score.
 */
/**
 * QP-2 (quality pass) — every qualifying fallback doc for a section,
 * best first. `pickBestFallbackDoc` is `[0]`; the multi-doc fill-gaps
 * merge in the bootstrap walks the tail to fill fields the primary doc
 * left empty. Ordering is deterministic: score desc, then doc id.
 */
export function rankFallbackDocs(
  docs: SiteDocument[],
  section: SedSectionId,
  reportType: string,
  // SED D0.3 — default aligned to the DCT match floor (0.4). Was 0.6.
  threshold = EXTRACTION_RELEVANCE_FLOOR,
): SiteDocument[] {
  const scored: Array<{ doc: SiteDocument; score: number }> = [];
  for (const d of docs) {
    if (!docQualifiesForFallback(d, section, reportType, threshold)) continue;
    const raw = d.relevance?.[reportType as never] as unknown;
    let score = typeof raw === 'number' ? raw : 0;
    // URE-2 — intent boost. When the doc carries `intendedRequirementIds`
    // AND one of those requirements maps to the target section via
    // REQUIREMENT_TO_SECTION_MAP, apply a +0.5 score bias so the
    // explicitly-intended doc wins over a generic high-relevance one.
    // Intent is a stronger signal than classifier score.
    if (d.intendedRequirementIds && d.intendedRequirementIds.length > 0) {
      for (const reqId of d.intendedRequirementIds) {
        const sections = REQUIREMENT_TO_SECTION_MAP[reqId];
        if (sections && sections.includes(section)) {
          score += 0.5;
          break;
        }
      }
    }
    scored.push({ doc: d, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.doc.id.localeCompare(b.doc.id))
    .map(s => s.doc);
}

export function pickBestFallbackDoc(
  docs: SiteDocument[],
  section: SedSectionId,
  reportType: string,
  threshold = EXTRACTION_RELEVANCE_FLOOR,
): SiteDocument | undefined {
  return rankFallbackDocs(docs, section, reportType, threshold)[0];
}

/* ── Confidence boost ────────────────────────────────────────────── */

/**
 * Compute the adjustedConfidence for a section given the provider's
 * baseline confidence + the strongest supporting doc's contribution.
 *
 *   adjusted = clamp(provider + 0.1 * docContribution, 0..1)
 *
 * Returns the trio so callers can stamp { adjustedConfidence,
 * docContributionScore, supportingDocIds } in one shot.
 */
export function computeAdjustedConfidence(
  providerConfidence: number,
  supportingDocs: SiteDocument[],
  reportType: string,
): { adjustedConfidence: number; docContributionScore: number } {
  if (supportingDocs.length === 0) {
    return { adjustedConfidence: providerConfidence, docContributionScore: 0 };
  }
  let best = 0;
  for (const d of supportingDocs) {
    const score = d.relevance?.[reportType as never];
    if (typeof score === 'number' && score > best) best = score;
  }
  const adjusted = Math.max(0, Math.min(1, providerConfidence + 0.1 * best));
  return { adjustedConfidence: adjusted, docContributionScore: best };
}

export interface ExtractRequirementFieldsInput {
  doc: SiteDocument;
  requirementId: string;
  runner?: typeof runGeminiJSON;
  /** Cancellation belongs to the actual model operation, not just publication. */
  abortSignal?: AbortSignal;
  /**
   * How the doc was matched to the requirement. `intent` = the uploader
   * declared it (`intendedRequirementIds`); `tag-match` = filename/tag
   * heuristics. Tag matches face a stricter survival floor below because
   * the match itself is a guess. Defaults from the doc's intents.
   */
  origin?: 'intent' | 'tag-match';
  /** Checklist authoredSchema — enables generic enum/date sanitation (D6). */
  authoredSchema?: readonly Pick<AuthoredFieldDef, 'key' | 'type' | 'enumValues'>[];
  /** Only a completed, valid model response that yielded no eligible fields. */
  onCompletedEmpty?: () => void;
  /** Preserve the actual failed call separately from a completed empty source. */
  onFailure?: (error: unknown) => void;
}

/**
 * Cheap preflight — would `extractRequirementFieldsFromDocument` even reach
 * the model for this (doc, requirement) pair? Exported so budget accounting
 * (cache layer / bootstrap pass) can skip ineligible pairs without spending
 * a miss on them. MUST mirror the wrapped function's own preflight.
 */
export function requirementExtractionEligible(
  doc: SiteDocument,
  requirementId: string,
): boolean {
  if (!getRequirementDocExtractSchema(requirementId)) return false;
  const text = (doc.textContent ?? '').trim();
  if (text.length < 50) return false;
  if (text.startsWith('[Binary file') || text.startsWith('[Image') || text.startsWith('[Geographic') || text.startsWith('[Video')) {
    return false;
  }
  return true;
}

export interface ExtractRequirementFieldsResult {
  fields: Record<string, StructuredEvidenceValue>;
  confidence: number;
  source: string;
  pageNumber?: number;
  sectionHeading?: string;
  excerptPreview?: string;
  /** P4 — stated provenance of the passage the fields came from. */
  attribution?: EvidenceAttribution;
}

const MAX_EXTRACTED_ARRAY_ITEMS = 25;
const SENSITIVE_ARCHAEOLOGY_KEY = /(lat(?:itude)?|lon(?:gitude)?|coordinates?|\butm\b|\bgps\b|precise.*location|resource.*location|street.*address)/i;
const SENSITIVE_ARCHAEOLOGY_LINE = /\b(?:lat(?:itude)?|lon(?:gitude)?|coordinates?|gps|utm|easting|northing|resource\s+(?:location|identifier|id)|site\s+(?:location|number|identifier|id)|trinomial|location\s+map|map\s+reference|street\s+address|quarter[-\s]?section|township\s+\d|range\s+\d|section\s+\d)\b/i;
const DECIMAL_COORDINATE_PAIR = /[-+]?(?:90(?:\.0+)?|[0-8]?\d(?:\.\d{3,})?)\s*[,/]\s*[-+]?(?:180(?:\.0+)?|1[0-7]\d(?:\.\d{3,})?|[0-9]?\d(?:\.\d{3,})?)/g;
const DMS_COORDINATE = /\b\d{1,3}\s*[°º]\s*\d{1,2}(?:\.\d+)?\s*['′]?\s*(?:\d{1,2}(?:\.\d+)?\s*["″]?)?\s*[NSEW]\b/gi;
const STREET_ADDRESS = /\b\d{1,6}\s+[A-Za-z0-9.'-]+(?:\s+[A-Za-z0-9.'-]+){0,5}\s+(?:street|st|road|rd|avenue|ave|boulevard|blvd|drive|dr|lane|ln|highway|hwy|route|rte|court|ct|way)\b/gi;

/**
 * Remove precise archaeological locations before text reaches a model,
 * persistence, logs, or report lineage. This is intentionally conservative:
 * aggregate scope/findings survive, while a location-bearing line is replaced
 * wholesale so nearby labels cannot be used to reconstruct the coordinate.
 */
export function redactArchaeologicalSensitiveLocations(value: string): string {
  let withheld = false;
  const safeLines: string[] = [];
  for (const rawLine of value.split(/\r?\n/)) {
    if (SENSITIVE_ARCHAEOLOGY_LINE.test(rawLine)) {
      withheld = true;
      continue;
    }
    const redacted = rawLine
      .replace(DECIMAL_COORDINATE_PAIR, () => {
        withheld = true;
        return '[SENSITIVE LOCATION WITHHELD]';
      })
      .replace(DMS_COORDINATE, () => {
        withheld = true;
        return '[SENSITIVE LOCATION WITHHELD]';
      })
      .replace(STREET_ADDRESS, () => {
        withheld = true;
        return '[SENSITIVE LOCATION WITHHELD]';
      });
    safeLines.push(redacted);
  }
  const compact = safeLines.join('\n').replace(/(?:\[SENSITIVE LOCATION WITHHELD\]\s*){2,}/g, '[SENSITIVE LOCATION WITHHELD]\n').trim();
  if (!withheld) return compact;
  return `${compact}${compact ? '\n' : ''}[SENSITIVE ARCHAEOLOGICAL LOCATION DETAILS WITHHELD]`;
}

function cleanExtractedString(value: string, maxLength: number): string | null {
  const clean = value.replace(/[\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return isMissingDocumentValue(clean) ? null : clean.slice(0, maxLength);
}

/**
 * Validate a requirement-field response before it reaches Firestore.
 * Unknown keys are dropped; arrays are primitive-only, deduplicated, and
 * bounded. Archaeological location keys are rejected even if a future model
 * ignores the response schema.
 */
export function sanitizeRequirementExtractedFields(
  requirementId: string,
  raw: Record<string, unknown>,
  allowedProperties: Record<string, unknown>,
  authoredSchema?: readonly Pick<AuthoredFieldDef, 'key' | 'type' | 'enumValues'>[],
): Record<string, StructuredEvidenceValue> {
  // D6 — generic schema-driven validation. The pre-D6 hardcoded per-key
  // branches (standardYear, ownershipStatus) covered exactly the keys
  // someone had already been burned by; every OTHER enum or date key passed
  // through unvalidated. Enum keys are canonicalized case-insensitively
  // against their declared values (drop on no match) and date keys must
  // parse. The schema DEFAULTS from the checklist so validation holds for
  // every caller — the explicit param remains as an override (tests,
  // schema-less requirements).
  const schemaByKey = new Map(
    (authoredSchema ?? checklistItemById(requirementId)?.authoredSchema ?? [])
      .map(f => [f.key, f] as const),
  );
  const fields: Record<string, StructuredEvidenceValue> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!(key in allowedProperties)) continue;
    if (key === 'pageNumber' || key === 'sectionHeading' || key === 'excerptPreview') continue;
    if ((ATTRIBUTION_KEYS as readonly string[]).includes(key)) continue;
    if (requirementId === 'archaeological-survey' && SENSITIVE_ARCHAEOLOGY_KEY.test(key)) continue;
    if (value === null || value === undefined) continue;

    if (Array.isArray(value)) {
      const schema = allowedProperties[key] as { type?: unknown; items?: { type?: unknown } } | undefined;
      if (schema?.type !== Type.ARRAY) continue;
      const expectedItemType = schema.items?.type;
      if (
        expectedItemType !== Type.STRING &&
        expectedItemType !== Type.NUMBER &&
        expectedItemType !== Type.BOOLEAN
      ) continue;
      const cleaned: Array<string | number | boolean> = [];
      for (const item of value.slice(0, MAX_EXTRACTED_ARRAY_ITEMS)) {
        if (expectedItemType === Type.STRING && typeof item === 'string') {
          const text = cleanExtractedString(item, 500);
          if (text) cleaned.push(text);
          continue;
        }
        if (expectedItemType === Type.NUMBER && typeof item === 'number' && Number.isFinite(item)) {
          cleaned.push(item);
          continue;
        }
        if (expectedItemType === Type.BOOLEAN && typeof item === 'boolean') cleaned.push(item);
      }
      if (cleaned.length === 0) continue;
      const unique = [...new Set(cleaned)];
      if (expectedItemType === Type.STRING) fields[key] = unique as string[];
      else if (expectedItemType === Type.NUMBER) fields[key] = unique as number[];
      else fields[key] = unique as boolean[];
      continue;
    }

    if (typeof value === 'string') {
      const safeValue = requirementId === 'archaeological-survey'
        ? redactArchaeologicalSensitiveLocations(value)
        : value;
      const text = cleanExtractedString(safeValue, 2_000);
      if (!text) continue;
      if (requirementId === 'archaeological-survey' && /^\[SENSITIVE/.test(text)) continue;
      // The pre-D6 hardcoded standardYear / ownershipStatus branches are
      // retired: both keys are `enum` in their checklist authoredSchema with
      // the exact same value sets, so the generic path below reproduces
      // their outcomes (ownershipStatus enumValues are lowercase — the
      // canonical casing IS the old `.toLowerCase()` behaviour).
      const fieldDef = schemaByKey.get(key);
      if (fieldDef?.type === 'enum' && fieldDef.enumValues?.length) {
        // Canonicalize to the schema's exact casing; drop unknown values —
        // an enum key with a non-member value is a hallucination, not data.
        const canonical = fieldDef.enumValues.find(
          v => v.toLowerCase() === text.toLowerCase(),
        );
        if (canonical === undefined) continue;
        fields[key] = canonical;
        continue;
      }
      if (fieldDef?.type === 'date') {
        // A date field carrying prose ("late 2024", "TBD") pollutes every
        // downstream date comparison. Require a parseable date.
        if (Number.isNaN(Date.parse(text))) continue;
      }
      fields[key] = text;
      continue;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) continue;
      const validation = validateValue(key, value);
      if (!validation.ok) continue;
      if ((key === 'resourceCount' && (value < 0 || !Number.isInteger(value))) ||
          (key === 'acreageSurveyed' && value < 0) ||
          (key === 'surveyedAcreage' && value < 0) ||
          (key === 'contourInterval_ft' && value <= 0)) continue;
      fields[key] = value;
      continue;
    }
    if (typeof value === 'boolean') fields[key] = value;
  }
  return fields;
}

/**
 * Extract authoredSchema-shaped fields from a user doc when the doc
 * was uploaded with matching `intendedRequirementIds`.
 */
/** A resource statement can be useful without constituting an electric service
 * letter. Admit only closed literal resource statements; keep all offer/date/
 * class fields out of the separate path without an electric-name anchor.
 * Full sentences (including soft wraps) are checked, so a clipped excerpt cannot
 * hide a governing denial, conditional or unknown. Complex prose can decline.
 */

export async function extractRequirementFieldsFromDocument(
  input: ExtractRequirementFieldsInput,
): Promise<ExtractRequirementFieldsResult | null> {
  throwIfAborted(input.abortSignal);
  const spec = getRequirementDocExtractSchema(input.requirementId);
  if (!spec) return null;
  // D1 — the intent gate is GONE. It required the uploader to have declared
  // `intendedRequirementIds` for this exact requirement, which defeated the
  // caller's tag-match expansion entirely (sedBootstrap passed tag-matched
  // docs in good faith and every one returned null here). Tag-matched docs
  // now extract too; the difference is the stricter survival floor below,
  // not a refusal to look.
  const intents = input.doc.intendedRequirementIds ?? [];
  const origin: 'intent' | 'tag-match' = input.origin
    ?? (intents.includes(input.requirementId) ? 'intent' : 'tag-match');

  // P1.4 — the whole body when captured (`fullExtractedText`), else the preview.
  const text = documentBodyText(input.doc).trim();
  if (text.length < 50) return null;
  if (text.startsWith('[Binary file') || text.startsWith('[Image') || text.startsWith('[Geographic') || text.startsWith('[Video')) {
    return null;
  }

  const runner = input.runner ?? runGeminiJSON;
  // D13 — never attach `spec.required` to the RESPONSE schema. A required
  // key in a Gemini response schema forces the model to emit SOMETHING for
  // it even when the document doesn't contain it — i.e. it mandates
  // fabrication, the exact opposite of "omit fields you cannot verify".
  // Required-ness is enforced post-hoc below: if none of the declared
  // required keys survive sanitization, the extraction is judged a miss.
  const schema = {
    ...spec.schema,
    required: [],
  } as Record<string, unknown>;

  try {
    const archaeologyProtected = input.requirementId === 'archaeological-survey';
    const promptText = archaeologyProtected
      ? redactArchaeologicalSensitiveLocations(text)
      : text;
    const raw = await runner<{ [key: string]: unknown }>({
      // Cost lever (strategy §4): requirement-field extraction is structured
      // field-pull — Flash when tiering is on, Pro when off.
      model: tieredModelFor('extraction'),
      contents:
        `Extract requirement "${input.requirementId}" report fields from the document below.\n` +
        `Return JSON matching the schema. Omit fields you cannot verify.\n` +
        'Keep the meaning of source measurements: municipal production is not aquifer yield; wetland delineation is not a boundary survey; mapped pipelines are not recorded easements; wetland indicator plants are not listed threatened/endangered species. Business introductions are not community feedback. Never invent a sentiment score from tone.\n' +
        (input.requirementId === 'utility-letter'
          ? 'Electric service fields require an electric utility source; water or sewer utilities do not qualify. Separately extract explicitly stated grid/pipeline resource distances, operator, and quantity with its unit and stated basis even when no electric utility is named. These partial resource facts do not constitute a utility service letter or establish firm delivery. capacityOffered_MW is utility-offered supply, never customer load, expansion demand, transformer MVA, or customer-owned equipment. serviceDate must be an explicitly stated electric service date, never the email or document date.\n'
          : '') +
        (input.requirementId === 'archaeological-survey'
          ? 'Never return or quote coordinates, addresses, site-location descriptions, maps, UTM/GPS values, or other precise archaeological locations. Return only aggregate scope, counts, findings, and case status.\n'
          : '') +
        `\n` +
        // The upload pipeline owns the body ceiling. A terminal empty receipt
        // must describe all captured source text, not an unseen 120k+ tail.
        `DOCUMENT FILENAME: ${archaeologyProtected ? 'protected-archaeological-survey-document' : input.doc.fileName}\n\n${promptText}`,
      schema: schema as never,
      signal: input.abortSignal,
    });
    throwIfAborted(input.abortSignal);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    const properties = ((spec.schema as { properties?: Record<string, unknown> }).properties ?? {});
    // authoredSchema defaults from the checklist INSIDE the sanitizer, so
    // passing the input through (possibly undefined) is enough for D6.
    let fields = sanitizeRequirementExtractedFields(
      input.requirementId, raw, properties, input.authoredSchema,
    );
    if (input.requirementId === 'utility-letter') {
      filterUtilityLetterFields(fields, text);
      const resources = sourceSupportedUtilityResourceFields(fields, text);
      for (const key of ['nearestTransmissionKm', 'substationDistKm', 'nearestPipelineKm',
        'nearestPipelineOperator', 'nearestPipelineMassFlowValue', 'nearestPipelineMassFlowUnit',
        'nearestPipelineMassFlowBasis']) {
        if (resources[key] === undefined) delete fields[key];
      }
    }
    filterRequirementFieldsBySource(input.requirementId, fields, text);
    if (Object.keys(fields).length === 0) {
      input.onCompletedEmpty?.();
      return null;
    }
    // The formal electric anchor stays required. A separately supported
    // resource-only partial response can survive without that anchor; this
    // stamps field provenance, never section data/status or formal fulfillment.
    if (spec.required.length > 0 && !spec.required.some(key => fields[key] !== undefined)) {
      fields = input.requirementId === 'utility-letter'
        ? sourceSupportedUtilityResourceFields(fields, text) : {};
      if (Object.keys(fields).length === 0) {
        input.onCompletedEmpty?.();
        return null;
      }
    }
    // D1 tag-match floor — a tag match is a guess, so with no declared
    // required key to anchor it, one stray surviving field is not enough
    // evidence that the guess was right. Intent-origin keeps the ≥1 floor:
    // the uploader TOLD us the doc answers this requirement.
    if (origin === 'tag-match' && spec.required.length === 0 && Object.keys(fields).length < 2) {
      input.onCompletedEmpty?.();
      return null;
    }

    // P4 — attribution is read, never persisted as a field.
    const requirementAttribution = archaeologyProtected ? undefined : readAttributionFromPayload(raw);
    return {
      fields,
      confidence: documentExtractionConfidence(input.doc),
      ...(requirementAttribution ? { attribution: requirementAttribution } : {}),
      source: archaeologyProtected
        ? 'Extracted from protected archaeological survey document'
        : `Extracted from "${cleanDocFileName(input.doc.fileName)}" for ${input.requirementId}`,
      pageNumber: typeof raw.pageNumber === 'number' ? raw.pageNumber : undefined,
      sectionHeading: !archaeologyProtected && typeof raw.sectionHeading === 'string' ? raw.sectionHeading : undefined,
      // Archaeology excerpts can accidentally contain a coordinate even when
      // the structured fields are safe. Suppress them wholesale.
      excerptPreview: !archaeologyProtected && typeof raw.excerptPreview === 'string'
        ? cleanExtractedString(raw.excerptPreview, 500) ?? undefined
        : undefined,
    };
  } catch (e) {
    rethrowIfAborted(input.abortSignal, e);
    console.warn(
      `[extractRequirementFieldsFromDocument] requirement=${input.requirementId} doc=${input.doc.id} failed:`,
      (e as Error)?.message,
    );
    input.onFailure?.(e);
    return null;
  }
}
